import { FC, FormEvent } from 'react'

import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import { EMAIL_REGEX, REGEX_EMAIL } from 'shared/constants/regex'
import { validatePhone } from 'shared/lib/validatePhone'
import { SelectOptions } from 'shared/models/general.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Input } from 'shared/ui/input'
import { Paper } from 'shared/ui/paper'
import { IPhoneInput, PhoneInput } from 'shared/ui/phone-input'
import { PopupContent } from 'shared/ui/poup-content'
import { Option } from 'shared/ui/select/select'

import SelectServiceForPassengers from './components/SelectServiceForPassengers'
import s from './styles.module.scss'

export interface FormCreateAdditionalService {
  topic?: string
  files?: File[]
  details?: string
  passengers?: Option[]
}

export interface IAdditionalServiceForm {
  email: string
  phone: IPhoneInput
  forms: FormCreateAdditionalService[]
}

interface Props {
  ticket?: MyTicketItem
  optionServices?: SelectOptions[]
  onCreateService?: (data: IAdditionalServiceForm) => void
}

export const AdditionalServiceForm: FC<Props> = ({
  ticket,
  optionServices,
  onCreateService,
}) => {
  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const passengers = ticket?.flight_details?.tickets[0]?.passengers

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IAdditionalServiceForm>({ defaultValues: { forms: [{}] } })

  const {
    fields: listFormsData,
    append,
    remove,
  } = useFieldArray({ control, name: 'forms' })

  const onChangeInputEmail = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value?.replace(REGEX_EMAIL, '')
  }

  const onSubmitHandler = handleSubmit((data) => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          button={t('continue')}
          title={t('sent_appeal')}
          description={t('feed_back.additional_description_before')}
          onClick={() => {
            closeDialog()
            onCreateService?.(data)
          }}
        />
      ),
    })
  })

  const onDelete = (index: number) => {
    remove(index)
  }

  const onAddService = () => {
    append({})
  }

  const listPassengers = passengers?.map((passenger) => ({
    value: `${passenger?.key}`,
    name: `${passenger?.name?.last || ''} ${passenger?.name?.first} ${
      passenger?.name?.middle || ''
    }`,
  }))

  const listAdditionalService = optionServices?.map((topic) => ({
    name: topic.name,
    value: topic.option,
  }))

  return (
    <form
      className={s.container}
      onSubmit={onSubmitHandler}
    >
      <div className={s.contact}>
        <Paper padding={[20]}>
          <p className={s.title}>{t('purchase_page.contact_title')}</p>

          <div className={s.contactField}>
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: validatePhone,
              }}
              render={({ field }) => (
                <PhoneInput
                  value={field?.value}
                  error={errors.phone?.phone}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </div>

          <div className={s.contactField}>
            <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: EMAIL_REGEX,
                  message: '',
                },
              }}
              render={({ field }) => (
                <Input
                  lang={'en'}
                  value={field.value}
                  error={errors?.email}
                  placeholder={t('email')}
                  onChange={(event) =>
                    field.onChange(onChangeInputEmail(event))
                  }
                />
              )}
            />
          </div>
        </Paper>
      </div>

      {listFormsData?.map((itemPassenger, index) => (
        <div
          key={itemPassenger.id}
          className={s.formPassenger}
        >
          <SelectServiceForPassengers
            index={index}
            errors={errors}
            control={control}
            id={itemPassenger.id}
            passenger={itemPassenger}
            listPassengers={listPassengers}
            listService={listAdditionalService}
            onDelete={() => onDelete(index)}
          />
        </div>
      ))}

      <div className={s.wrapperButton}>
        <Button
          type="button"
          onClick={onAddService}
          variant="transparent"
        >
          <div className={s.buttonAdd}>
            <AppIcon
              id={AllIcons.PLUS}
              height={24}
              width={24}
              className={s.icon}
            />
            {t('add_service_text')}
          </div>
        </Button>
      </div>

      <ButtonContinue>
        <span className={s.text}>{t('sent')}</span>
      </ButtonContinue>
    </form>
  )
}
